import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import React, { useState } from "react";
import { useMode } from "../../mode/ModeContext";
import { modes } from "../../mode/modes";
import {
  deleteSessionFromHistory,
  SessionHistoryEntry,
} from "../../utils/localStorage";

interface SessionCarouselProps {
  sessions: SessionHistoryEntry[];
  onSessionsChanged: () => void;
}

function formatRelativeTime(isoDate: string): string {
  const now = new Date();
  const date = new Date(isoDate);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export default function SessionCarousel({
  sessions,
  onSessionsChanged,
}: SessionCarouselProps) {
  const { resumeSession } = useMode();
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  if (sessions.length === 0) return null;

  const handleDelete = (id: string) => {
    deleteSessionFromHistory(id);
    setDeleteTarget(null);
    onSessionsChanged();
  };

  return (
    <Box sx={{ mb: 4, mx: { xs: -2, sm: 0 } }}>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, mb: 1.5, px: { xs: 2, sm: 0 } }}
      >
        Recent Sessions
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          py: 0.5,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          // Keep first card aligned with page content margin
          pl: { xs: 2, sm: 0 },
          scrollPaddingLeft: { xs: "16px", sm: 0 },
          // Pseudo-element to preserve right padding inside the scroll container
          "&::after": {
            content: '""',
            minWidth: { xs: 16, sm: 0 },
            flexShrink: 0,
          },
        }}
      >
        {sessions.map((session) => {
          const modeDef = modes[session.mode];
          const background =
            modeDef?.color1 && modeDef?.color2
              ? `linear-gradient(135deg, ${modeDef.color1}, ${modeDef.color2})`
              : modeDef?.color1 || grey[600];

          return (
            <Card
              key={session.id}
              sx={{
                width: 240,
                flexShrink: 0,
                scrollSnapAlign: "start",
                position: "relative",
              }}
            >
              <IconButton
                size="small"
                aria-label="Delete session"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(session.id);
                }}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  zIndex: 1,
                  color: "rgba(255,255,255,0.7)",
                  "&:hover": {
                    color: "rgba(255,255,255,1)",
                    backgroundColor: "rgba(0,0,0,0.15)",
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <CardActionArea
                onClick={() => resumeSession(session)}
                sx={{
                  height: "100%",
                  background,
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "stretch",
                }}
              >
                <CardContent
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    py: 1.5,
                    "&:last-child": { pb: 1.5 },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    {modeDef?.icon && (
                      <modeDef.icon
                        style={{
                          verticalAlign: "text-bottom",
                          marginRight: 6,
                          fontSize: "1.1rem",
                        }}
                      />
                    )}
                    {modeDef?.label ?? session.mode}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                      {session.simulations.length} simulation
                      {session.simulations.length !== 1 ? "s" : ""}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.7, display: "block" }}
                    >
                      {formatRelativeTime(session.updatedAt)}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Delete session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This session will be permanently deleted. This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            onClick={() => deleteTarget && handleDelete(deleteTarget)}
            variant="contained"
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
